import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Bus, Truck, Package, Car, ArrowLeft, Leaf, Zap, Trophy, Target,
  Check, Mail, Shield, Send, Loader2, ChevronDown, ChevronRight,
  Clock, Users, TrendingUp, Phone, Euro, Fuel, Award, Calendar,
  AlertTriangle, HelpCircle, X, ArrowRight, Star, BadgeCheck, Flame, RefreshCw,
  Wrench, Activity, ExternalLink
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MOBILSAFE_LOGO = "/logo-mobilsafe.png";

const CAESLandingNew = ({ config, onBack }) => {
  const [currentView, setCurrentView] = useState('landing'); // 'landing', 'pre-registro', 'calculator', 'partial-result', 'select-appointment', 'confirmation'
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [formData, setFormData] = useState({
    numVehicles: 50,
    kmAnuales: 45000,
    consumoMedio: 28,
    ahorroPercent: 7.5,
    sector: 'logistica'
  });
  const [caesEstimado, setCaesEstimado] = useState(0);
  const [leadData, setLeadData] = useState({ empresa: '', email: '', telefono: '' });
  const [sending, setSending] = useState(false);
  // IMPL-060 · verificación de email por código (OTP) antes de mostrar el resultado
  const [verificacionId, setVerificacionId] = useState(null);
  const [emailMascara, setEmailMascara] = useState('');
  const [codigo, setCodigo] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [faqs, setFaqs] = useState([]);
  
  // Estados para el reveal animado
  const [showReveal, setShowReveal] = useState(false);
  const [animatedMoney, setAnimatedMoney] = useState(0);
  const [animatedLiters, setAnimatedLiters] = useState(0);
  
  // Estados para el sistema de citas
  const [leadId, setLeadId] = useState(null);
  const [disponibilidad, setDisponibilidad] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [citaConfirmada, setCitaConfirmada] = useState(null);
  
  // Datos MITECO
  const [datosMiteco, setDatosMiteco] = useState({
    gwhTotalCertificado: 13366,
    actuacionesTotales: 23002,
    actuacionesTRA020: 272
  });

  // Cargar datos MITECO desde la API
  useEffect(() => {
    const loadDatosMiteco = async () => {
      try {
        const response = await axios.get(`${API}/config/miteco`);
        if (response.data) {
          setDatosMiteco({
            gwhTotalCertificado: response.data.gwhTotalCertificado || 13366,
            actuacionesTotales: response.data.actuacionesTotales || 23002,
            actuacionesTRA020: response.data.actuacionesTRA020 || 272
          });
        }
      } catch (error) {
        console.log('Usando datos MITECO por defecto');
      }
    };
    loadDatosMiteco();
  }, []);

  // Cargar FAQs desde la API
  useEffect(() => {
    const loadFaqs = async () => {
      try {
        const response = await axios.get(`${API}/faqs/caes`);
        if (response.data?.faqs) {
          setFaqs(response.data.faqs.map(f => ({
            q: f.pregunta,
            a: f.respuesta
          })));
        }
      } catch (error) {
        // Si falla, usar FAQs por defecto
        setFaqs([
          { q: "¿Qué son los certificados CAES?", a: "Los CAES son un instrumento del Sistema Nacional de Obligaciones de Eficiencia Energética..." },
          { q: "¿Cómo funciona el programa?", a: "Instalamos tecnología y formamos a los conductores durante 90 días..." }
        ]);
      }
    };
    loadFaqs();
  }, []);

  // Scroll arriba cuando cambia la vista
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentView]);

  // Efecto para activar el reveal después de 2 segundos en partial-result
  useEffect(() => {
    if (currentView === 'partial-result') {
      setShowReveal(false);
      setAnimatedMoney(0);
      setAnimatedLiters(0);
      
      const litrosAhorrados = Math.round((formData.numVehicles * formData.kmAnuales / 100) * formData.consumoMedio * (formData.ahorroPercent / 100));
      const conMobilsafe = Math.round(caesEstimado * 1.5);
      
      const timer = setTimeout(() => {
        setShowReveal(true);
        // Animar los contadores
        const duration = 1500;
        const steps = 60;
        let currentStep = 0;
        
        const interval = setInterval(() => {
          currentStep++;
          const progress = currentStep / steps;
          const easeProgress = 1 - Math.pow(1 - progress, 3);
          setAnimatedMoney(Math.round(conMobilsafe * easeProgress));
          setAnimatedLiters(Math.round(litrosAhorrados * easeProgress));
          
          if (currentStep >= steps) {
            clearInterval(interval);
          }
        }, duration / steps);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [currentView, caesEstimado, formData]);

  const calcularCAES = () => {
    // Cálculo real de CAES según backend
    const litrosAnuales = (formData.numVehicles * formData.kmAnuales / 100) * formData.consumoMedio;
    const litrosAhorrados = litrosAnuales * (formData.ahorroPercent / 100);
    const kwhAhorrados = litrosAhorrados * 10; // KWH_POR_LITRO = 10
    const mwhAhorrados = kwhAhorrados / 1000;
    const caesBruto = mwhAhorrados * 145; // PRECIO_MWH = 145
    const caesNeto = caesBruto * 0.8; // Menos 20% comisión
    const caesNetoRound = Math.round(caesNeto);
    setCaesEstimado(caesNetoRound);

    // IMPL-060 · el cálculo ya está hecho pero el resultado NO se muestra aún:
    // primero pedimos los datos y verificamos el email con un código.
    setCurrentView('pre-registro');
  };

  // IMPL-060 · cuenta atrás para habilitar el reenvío del código
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleVerificarCodigo = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const valor = (codigo || '').replace(/\D/g, '');
    if (valor.length !== 6) {
      toast.error('Introduce los 6 dígitos del código');
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/leads-caes/verificacion/confirmar`, {
        verificacion_id: verificacionId,
        codigo: valor,
      });
      setLeadId(data.lead_id);
      toast.success('Email verificado. Aquí tienes tu cálculo.');
      setCurrentView('partial-result');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No hemos podido verificar el código');
    }
    setSending(false);
  };

  const handleReenviarCodigo = async () => {
    if (cooldown > 0) return;
    try {
      const { data } = await axios.post(`${API}/leads-caes/verificacion/reenviar`, {
        verificacion_id: verificacionId,
      });
      setEmailMascara(data.email_mascara || emailMascara);
      setCodigo('');
      setCooldown(60);
      toast.success('Código reenviado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No hemos podido reenviar el código');
    }
  };

  const handleSubmitLead = async () => {
    // IMPL-058 · El lead ya fue creado en pre-registro (handlePreRegistroSubmit)
    // y actualizado con el cálculo real en calcularCAES. Aquí solo cargamos
    // los slots disponibles y avanzamos a select-appointment. La validación
    // (!email || !telefono) se mantiene como safety net por si alguien llega
    // a esta función saltándose el pre-registro.
    if (!leadData.email || !leadData.telefono) {
      toast.error('Completa todos los campos');
      return;
    }
    setSending(true);
    try {
      // Cargar slots disponibles
      setLoadingSlots(true);
      const slotsResponse = await axios.get(`${API}/citas/disponibilidad`);
      setDisponibilidad(slotsResponse.data.disponibilidad);
      setLoadingSlots(false);

      setCurrentView('select-appointment');
    } catch (error) {
      toast.error('Error cargando slots. Inténtalo de nuevo.');
    }
    setSending(false);
  };

  const handleConfirmAppointment = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error('Selecciona una fecha y hora');
      return;
    }
    setSending(true);
    try {
      const response = await axios.post(`${API}/citas`, {
        lead_id: leadId,
        fecha: selectedDate,
        hora: selectedTime
      });
      setCitaConfirmada({
        fecha: selectedDate,
        hora: selectedTime,
        diaSemana: disponibilidad.find(d => d.fecha === selectedDate)?.diaSemana || ''
      });
      toast.success('¡Cita confirmada!');
      setCurrentView('confirmation');
    } catch (error) {
      if (error.response?.data?.detail === 'Este horario ya no está disponible') {
        toast.error('Este horario acaba de ser reservado. Elige otro.');
        // Recargar disponibilidad
        const slotsResponse = await axios.get(`${API}/citas/disponibilidad`);
        setDisponibilidad(slotsResponse.data.disponibilidad);
      } else {
        toast.error('Error al confirmar la cita. Inténtalo de nuevo.');
      }
    }
    setSending(false);
  };

  const goToCalculator = () => {
    // IMPL-060 · el usuario calcula primero (sin fricción) y los datos se piden
    // justo antes de revelar el resultado, con verificación de email.
    setCurrentView('calculator');
  };

  // IMPL-060 · El muro de datos ocurre DESPUÉS del cálculo y ANTES del resultado.
  // Validación dura en backend (formato, dominios desechables, patrones basura,
  // MX del dominio, teléfono español) + envío de código al email. El resultado
  // solo se desbloquea con el código, así que un email falso no sirve de nada.
  const handlePreRegistroSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const email = (leadData.email || '').trim();
    const telefono = (leadData.telefono || '').trim();
    if (!email || !telefono) {
      toast.error('Email y teléfono son obligatorios');
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/leads-caes/verificacion/solicitar`, {
        ...formData,
        caesEstimado: caesEstimado,
        empresa: leadData.empresa || '',
        email,
        telefono,
        web: honeypot,
      });
      if (data.enviado === false) {
        // El servidor no ha enviado el código (p. ej. honeypot relleno por el
        // autorrelleno del navegador). Nunca dejamos a la persona esperando
        // un código que no va a llegar.
        setHoneypot('');
        toast.error('No hemos podido enviar el código. Vuelve a intentarlo.');
        setSending(false);
        return;
      }
      setVerificacionId(data.verificacion_id);
      setEmailMascara(data.email_mascara || email);
      setCodigo('');
      setCooldown(60);
      setCurrentView('verificacion');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No hemos podido enviar el código. Revisa tus datos.');
    }
    setSending(false);
  };

  // ==================== VISTA: VERIFICACIÓN DE EMAIL (IMPL-060) ====================
  if (currentView === 'verificacion') {
    return (
      <div className="min-h-screen bg-white text-slate-800" data-testid="caes-verificacion-view">
        <header style={{ background: '#0a6640' }}>
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
            <button
              onClick={() => setCurrentView('pre-registro')}
              data-testid="verificacion-back-btn"
              className="p-2 hover:bg-white/10 rounded-lg transition-all"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.15)' }}>
              <Mail className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-base sm:text-lg font-black text-white leading-tight">VERIFICA TU EMAIL</h1>
              <p className="text-xs text-white/60">Último paso para ver tu cálculo</p>
            </div>
          </div>
        </header>

        <div className="max-w-md mx-auto" style={{ padding: '32px 24px' }}>
          <h2 style={{ fontSize: '23px', fontWeight: 900, color: '#1e293b', lineHeight: 1.3 }} data-testid="verificacion-headline">
            Te hemos enviado un código a<br />
            <span style={{ color: '#0a6640' }} data-testid="verificacion-email-mascara">{emailMascara}</span>
          </h2>
          <p className="mt-3 mb-7" style={{ fontSize: '14px', color: '#64748b', lineHeight: 1.6 }}>
            Introduce los 6 dígitos para desbloquear tu estimación de ingresos CAES.
            El código caduca en 15 minutos.
          </p>

          <form onSubmit={handleVerificarCodigo} className="space-y-4">
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              data-testid="verificacion-codigo-input"
              className="w-full text-center focus:outline-none transition-all"
              style={{
                padding: '16px', fontSize: '30px', fontWeight: 900, letterSpacing: '0.35em',
                border: '1.5px solid #e2e8f0', borderRadius: '12px', background: '#f8fafc', color: '#0a6640',
              }}
            />

            <button
              type="submit"
              disabled={sending}
              data-testid="verificacion-submit"
              className="w-full text-white flex items-center justify-center gap-2 transition-all hover:opacity-95 disabled:opacity-50"
              style={{ padding: '14px', fontSize: '15px', fontWeight: 900, letterSpacing: '0.04em', borderRadius: '12px', background: 'linear-gradient(135deg, #0a6640, #12a362)' }}
            >
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <BadgeCheck className="w-5 h-5" />}
              {sending ? 'Verificando...' : 'Ver mi Ingreso CAES'}
            </button>
          </form>

          <div className="flex items-center justify-between mt-5">
            <button
              onClick={handleReenviarCodigo}
              disabled={cooldown > 0}
              data-testid="verificacion-reenviar"
              className="transition-all disabled:opacity-40"
              style={{ fontSize: '13px', fontWeight: 700, color: '#0a6640' }}
            >
              {cooldown > 0 ? `Reenviar código (${cooldown}s)` : 'Reenviar código'}
            </button>
            <button
              onClick={() => setCurrentView('pre-registro')}
              data-testid="verificacion-cambiar-email"
              style={{ fontSize: '13px', color: '#64748b' }}
            >
              Cambiar email
            </button>
          </div>

          <p className="mt-7 pt-5 border-t" style={{ fontSize: '12px', color: '#94a3b8', lineHeight: 1.6, borderTopColor: '#f1f5f9' }}>
            Verificamos el email porque no te damos solo una cifra: analizamos tu flota y gestionamos
            los datos dentro de nuestro proceso completo de mejora. Por eso certificamos un porcentaje
            muy superior al de las consultoras que solo recogen datos.
          </p>
          <p className="mt-3" style={{ fontSize: '12px', color: '#94a3b8' }}>
            ¿No te llega? Revisa la carpeta de spam o escríbenos.
          </p>
        </div>

        <footer className="py-6 text-center">
          <p style={{ fontSize: '12px', color: '#94a3b8' }}>© 2026 MOBILSAFE Fleet Technology. Certificados CAES</p>
        </footer>
      </div>
    );
  }

  // ==================== VISTA: MURO DE DATOS (IMPL-060) ====================
  // El cálculo ya está hecho; aquí pedimos los datos y verificamos el email.
  if (currentView === 'pre-registro') {
    return (
      <div className="min-h-screen bg-white text-slate-800" data-testid="caes-pre-registro-view">
        {/* Header verde — igual que el resto de la web */}
        <header style={{ background: '#0a6640' }}>
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
            <button
              onClick={() => setCurrentView('calculator')}
              data-testid="pre-registro-back-btn"
              className="p-2 hover:bg-white/10 rounded-lg transition-all"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.15)' }}>
              <Flame className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-base sm:text-lg font-black text-white leading-tight">TU CÁLCULO ESTÁ LISTO</h1>
              <p className="text-xs text-white/60">Certificados de Ahorro Energético</p>
            </div>
          </div>
        </header>

        {/* Body */}
        <div className="max-w-xl mx-auto" style={{ padding: '28px 24px' }}>
          {/* Aviso de cálculo listo */}
          <div
            className="flex items-center gap-3 mb-5"
            style={{ background: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: '12px', padding: '13px 15px' }}
            data-testid="pre-registro-calculo-listo"
          >
            <BadgeCheck className="w-5 h-5" style={{ color: '#0a6640', flexShrink: 0 }} />
            <p style={{ fontSize: '13px', color: '#166534', lineHeight: 1.5 }}>
              Hemos calculado el potencial CAES de tus <strong>{formData.numVehicles} vehículos</strong>.
              Te lo mostramos en cuanto verifiquemos tu email.
            </p>
          </div>

          {/* Headline */}
          <h2
            className="leading-snug mb-1.5"
            style={{ fontSize: '24px', fontWeight: 900, color: '#1e293b' }}
            data-testid="pre-registro-headline"
          >
            Otros solo te dan una cifra.<br />
            Nosotros <span style={{ color: '#ec561c' }}>gestionamos</span> el resultado de tu flota.
          </h2>

          {/* Subheadline · diferencial */}
          <p
            className="pb-6 mb-6 border-b"
            style={{ fontSize: '14px', color: '#64748b', lineHeight: 1.6, borderBottomColor: '#f1f5f9' }}
          >
            La mayoría de consultoras se limitan a recoger datos y tramitar. Nosotros metemos esos
            datos en nuestro proceso completo de mejora de flota, y por eso certificamos un
            porcentaje muy superior al del mercado. Por eso necesitamos un contacto real: para
            poder analizar tu caso, no para enviarte un PDF automático.
          </p>

          {/* CTA copy */}
          <p
            className="mb-5"
            style={{ fontSize: '15px', fontWeight: 700, color: '#1e293b', lineHeight: 1.5 }}
          >
            Dinos dónde te enviamos tu resultado<br />
            <span style={{ color: '#ec561c' }}>+ la guía "Los 6 errores que te hacen perder dinero"</span>
          </p>

          {/* Formulario */}
          <form onSubmit={handlePreRegistroSubmit} className="space-y-3.5">
            {/* Honeypot anti-bot: invisible para humanos. El nombre NO puede
                parecerse a «web/url/empresa»: el autorrelleno del navegador y
                los gestores de contraseñas lo rellenaban y tumbaban leads
                reales antes de enviar el código. */}
            <input
              type="text"
              name="cx_ref"
              id="cx_ref"
              tabIndex={-1}
              autoComplete="off"
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
              data-testid="caes-honeypot"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
            />
            <div>
              <label
                className="block mb-1.5 uppercase"
                style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', letterSpacing: '0.05em' }}
              >
                Empresa <span style={{ fontSize: '10px', color: '#94a3b8', marginLeft: 4, textTransform: 'none', letterSpacing: 0 }}>(opcional)</span>
              </label>
              <input
                type="text"
                value={leadData.empresa}
                onChange={(e) => setLeadData({ ...leadData, empresa: e.target.value })}
                placeholder="Transportes García S.L."
                data-testid="pre-registro-empresa"
                className="w-full focus:outline-none transition-all"
                style={{ padding: '11px 14px', fontSize: '14px', border: '1.5px solid #e2e8f0', borderRadius: '10px', background: '#f8fafc', color: '#1e293b' }}
              />
            </div>
            <div>
              <label
                className="block mb-1.5 uppercase"
                style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', letterSpacing: '0.05em' }}
              >
                Email *
              </label>
              <input
                type="email"
                required
                value={leadData.email}
                onChange={(e) => setLeadData({ ...leadData, email: e.target.value })}
                placeholder="tu@empresa.es"
                data-testid="pre-registro-email"
                className="w-full focus:outline-none transition-all"
                style={{ padding: '11px 14px', fontSize: '14px', border: '1.5px solid #e2e8f0', borderRadius: '10px', background: '#f8fafc', color: '#1e293b' }}
              />
            </div>
            <div>
              <label
                className="block mb-1.5 uppercase"
                style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', letterSpacing: '0.05em' }}
              >
                Teléfono *
              </label>
              <input
                type="tel"
                value={leadData.telefono}
                onChange={(e) => setLeadData({ ...leadData, telefono: e.target.value })}
                placeholder="+34 600 000 000"
                data-testid="pre-registro-telefono"
                className="w-full focus:outline-none transition-all"
                style={{ padding: '11px 14px', fontSize: '14px', border: '1.5px solid #e2e8f0', borderRadius: '10px', background: '#f8fafc', color: '#1e293b' }}
              />
            </div>

            <button
              type="submit"
              disabled={sending}
              data-testid="pre-registro-submit"
              className="w-full text-white flex items-center justify-center gap-2 transition-all hover:opacity-95 mt-1.5 disabled:opacity-50"
              style={{ padding: '14px', fontSize: '15px', fontWeight: 900, letterSpacing: '0.04em', borderRadius: '12px', background: 'linear-gradient(135deg, #0a6640, #12a362)' }}
            >
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Zap className="w-5 h-5" />}
              {sending ? 'Enviando código...' : 'Ver mi Ingreso CAES'}
            </button>

            <p className="text-center mt-2.5" style={{ fontSize: '11px', color: '#94a3b8' }}>
              🔒 Te enviaremos un código a tu email para confirmarlo. Sin spam.
            </p>
          </form>
        </div>

        {/* Footer */}
        <footer className="py-6 text-center">
          <p style={{ fontSize: '12px', color: '#94a3b8' }}>© 2026 MOBILSAFE Fleet Technology. Certificados CAES</p>
        </footer>
      </div>
    );
  }

  // ==================== VISTA: CALCULADORA ====================
  if (currentView === 'calculator') {
    return (
      <div className="min-h-screen bg-white text-slate-800" data-testid="caes-calculator-view">
        {/* Header verde MOBILSAFE — IMPL-056 (antes era rojo/naranja) */}
        <header style={{ background: '#0a6640' }}>
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
            <button onClick={() => setCurrentView('landing')} className="p-2 hover:bg-white/10 rounded-lg transition-all" data-testid="calculator-back-btn">
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.15)' }}>
              <RefreshCw className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-base sm:text-lg font-black text-white leading-tight">CALCULA TU INGRESO CAES</h1>
              <p className="text-xs text-white/60">Certificados de Ahorro Energético</p>
            </div>
          </div>
        </header>

        {/* Calculator Form */}
        <div className="max-w-xl mx-auto px-6 py-10">
          {/* Título provocador */}
          <div className="text-center mb-6">
            <h2 className="text-3xl md:text-4xl font-black italic leading-tight" style={{ color: '#1e293b' }}>
              ¿Cuánto dinero estás<br/>
              <span style={{ color: '#ec561c' }}>dejando de cobrar</span>?
            </h2>
            <p className="mt-2" style={{ fontSize: '12px', color: '#94a3b8' }} data-testid="calculator-aprox-note">
              Datos aproximados · puedes ajustarlos
            </p>
          </div>

          {/* Formulario */}
          <div className="rounded-2xl p-6 space-y-5" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
            {/* Número de vehículos */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: '#64748b' }}>
                Nº de Vehículos
              </label>
              <input
                type="number"
                value={formData.numVehicles}
                onChange={(e) => setFormData({...formData, numVehicles: parseInt(e.target.value) || 0})}
                className="w-full px-4 py-3 bg-white rounded-xl text-2xl font-bold text-center focus:outline-none focus:ring-2 transition-all"
                style={{ border: '1.5px solid #e2e8f0', color: '#1e293b' }}
                placeholder="50"
              />
            </div>

            {/* Km anuales */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: '#64748b' }}>
                Km/año por vehículo <span style={{ fontSize: '10px', color: '#94a3b8', marginLeft: 4 }}>aprox.</span>
              </label>
              <input
                type="number"
                value={formData.kmAnuales}
                onChange={(e) => setFormData({...formData, kmAnuales: parseInt(e.target.value) || 0})}
                className="w-full px-4 py-3 bg-white rounded-xl text-2xl font-bold text-center focus:outline-none focus:ring-2 transition-all"
                style={{ border: '1.5px solid #e2e8f0', color: '#1e293b' }}
                placeholder="45000"
              />
            </div>

            {/* Consumo medio */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: '#64748b' }}>
                Consumo medio (L/100km) <span style={{ fontSize: '10px', color: '#94a3b8', marginLeft: 4 }}>aprox.</span>
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.consumoMedio}
                onChange={(e) => setFormData({...formData, consumoMedio: parseFloat(e.target.value) || 0})}
                className="w-full px-4 py-3 bg-white rounded-xl text-2xl font-bold text-center focus:outline-none focus:ring-2 transition-all"
                style={{ border: '1.5px solid #e2e8f0', color: '#1e293b' }}
                placeholder="28"
              />
            </div>

            {/* % Ahorro combustible — IMPL-056 precargado a 7.5 con nota */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: '#64748b' }}>
                % Ahorro combustible
              </label>
              <input
                type="number"
                step="0.1"
                min="1"
                max="20"
                value={formData.ahorroPercent}
                onChange={(e) => setFormData({...formData, ahorroPercent: parseFloat(e.target.value) || 7.5})}
                data-testid="calculator-ahorro-input"
                className="w-full px-4 py-3 bg-white rounded-xl text-2xl font-bold text-center focus:outline-none focus:ring-2 transition-all"
                style={{ border: '1.5px solid #e2e8f0', color: '#1e293b' }}
                placeholder="7.5"
              />
              <p className="mt-1" style={{ fontSize: '10px', color: '#0a6640', fontStyle: 'italic', fontWeight: 600 }} data-testid="calculator-ahorro-media">
                * La media de las flotas ronda el 7,5%
              </p>
            </div>

            {/* Tipo de Flota */}
            <div>
              <label className="block text-sm font-semibold mb-3" style={{ color: '#64748b' }}>
                Tipo de Flota
              </label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: 'urbano', label: 'Transporte Urbano', icon: <Bus className="w-6 h-6" /> },
                  { value: 'interurbano', label: 'Transporte Interurbano', icon: <Truck className="w-6 h-6" /> },
                  { value: 'distribucion', label: 'Distribución', icon: <Package className="w-6 h-6" /> },
                  { value: 'logistica', label: 'Logística', icon: <Truck className="w-6 h-6" /> },
                  { value: 'taxi', label: 'Taxi/VTC', icon: <Car className="w-6 h-6" /> },
                ].map((sector) => {
                  const active = formData.sector === sector.value;
                  return (
                    <button
                      key={sector.value}
                      onClick={() => setFormData({...formData, sector: sector.value})}
                      className="p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2"
                      style={{
                        background: active ? '#f0fdf4' : 'white',
                        borderColor: active ? '#0a6640' : '#e2e8f0',
                        color: active ? '#0a6640' : '#64748b',
                        fontWeight: 600,
                      }}
                    >
                      {sector.icon}
                      <span className="text-sm font-semibold text-center">{sector.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* CTA Calcular — naranja MOBILSAFE con text-white explícito */}
          <button
            onClick={calcularCAES}
            data-testid="calculator-submit-btn"
            className="w-full mt-6 py-5 text-white rounded-xl font-black text-xl uppercase tracking-wide shadow-xl transition-all flex items-center justify-center gap-3 hover:opacity-95"
            style={{ background: 'linear-gradient(135deg, #ec561c, #d44a15)' }}
          >
            <Zap className="w-6 h-6" />
            Ver mi Ingreso CAES
          </button>

          <p className="text-center mt-4 italic" style={{ fontSize: '12px', color: '#94a3b8' }}>
            * Cálculo orientativo. El exacto se hace en la reunión.
          </p>
        </div>

        {/* Footer */}
        <footer className="py-6 text-center">
          <p style={{ fontSize: '12px', color: '#94a3b8' }}>© 2026 MOBILSAFE Fleet Technology. Certificados CAES</p>
        </footer>
      </div>
    );
  }

  // ==================== VISTA: RESULTADO PARCIAL (con diferenciación) ====================
  if (currentView === 'partial-result') {
    const rangoMin = Math.round(caesEstimado * 0.85).toLocaleString('es-ES');
    const rangoMax = Math.round(caesEstimado * 1.15).toLocaleString('es-ES');

    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 via-orange-800 to-red-900 text-white">
        {/* Estilos para animación heartbeat */}
        <style>{`
          @keyframes heartbeat {
            0% { transform: scale(1); }
            14% { transform: scale(1.08); }
            28% { transform: scale(1); }
            42% { transform: scale(1.08); }
            70% { transform: scale(1); }
            100% { transform: scale(1); }
          }
          .heartbeat {
            animation: heartbeat 1.5s ease-in-out infinite;
          }
          .heartbeat-delay {
            animation: heartbeat 1.5s ease-in-out infinite;
            animation-delay: 0.1s;
          }
        `}</style>

        {/* Header */}
        <header className="bg-black/20 backdrop-blur-lg">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
            <button onClick={() => setCurrentView('calculator')} className="p-2 hover:bg-white/10 rounded-lg transition-all">
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center shadow-lg">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-black text-white">TU INGRESO CAES</h1>
              <p className="text-sm text-orange-200/70">Certificados de Ahorro Energético</p>
            </div>
          </div>
        </header>

        <div className="max-w-xl mx-auto px-6 py-8">
          {/* ========== SECCIÓN 1: LO NORMAL ========== */}
          <div className="mb-8">
            <div className="flex justify-center mb-4">
              <div className="inline-flex items-center gap-2 bg-slate-500/30 border border-slate-400/50 px-5 py-2 rounded-full">
                <TrendingUp className="w-4 h-4 text-slate-300" />
                <span className="text-slate-300 font-semibold text-sm">ESTO ES LO NORMAL</span>
              </div>
            </div>

            <p className="text-center text-lg text-orange-100/80 mb-4">
              Tu flota de <span className="font-bold text-white">{formData.numVehicles} vehículos</span> generaría<br/>
              con cualquier gestor CAES:
            </p>

            <div className="bg-slate-800/50 border border-slate-500/30 rounded-2xl p-6 text-center">
              <div className="flex items-center justify-center gap-3 mb-2">
                <span className="text-3xl md:text-4xl font-black text-slate-300">{rangoMin} €</span>
                <span className="text-xl text-slate-500">-</span>
                <span className="text-3xl md:text-4xl font-black text-slate-300">{rangoMax} €</span>
              </div>
              <p className="text-slate-400 text-sm">en certificados CAES</p>
            </div>
          </div>

          {/* ========== SECCIÓN 2: MOBILSAFE CON REVEAL ========== */}
          <div className="mb-8">
            <div className="flex justify-center mb-4">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-500/30 to-orange-500/30 border border-yellow-400/50 px-5 py-2 rounded-full">
                <Award className="w-4 h-4 text-yellow-400" />
                <span className="text-yellow-400 font-bold text-sm">PERO NOSOTROS NO SOMOS LO NORMAL</span>
              </div>
            </div>

            {/* Contenedor con posición relativa para el overlay */}
            <div className="relative bg-gradient-to-br from-orange-500/30 to-red-500/30 border-2 border-yellow-400/50 rounded-2xl p-8 text-center min-h-[220px] overflow-hidden">
              
              {/* Contenido base: "El doble" con heartbeat — IMPL-056 (antes era +50%) */}
              <div className={`transition-all duration-500 ${showReveal ? 'opacity-0 scale-90' : 'opacity-100 scale-100'}`}>
                <p className="text-5xl md:text-6xl font-black text-yellow-400 heartbeat" style={{textShadow: '0 0 40px rgba(250,204,21,0.5)'}} data-testid="partial-result-doble">
                  El doble
                </p>
                <p className="text-lg md:text-xl font-bold text-yellow-300 mt-3 heartbeat-delay px-2">
                  CAES maximizados + transformación de flota
                </p>
              </div>

              {/* Overlay reveal que tapa */}
              <div className={`absolute inset-0 bg-gradient-to-br from-black/95 to-zinc-900/95 rounded-xl flex flex-col items-center justify-center p-6 transition-all duration-600 ${showReveal ? 'opacity-100 scale-100' : 'opacity-0 scale-80 pointer-events-none'}`}
                   style={{boxShadow: showReveal ? '0 0 30px rgba(250,204,21,0.3)' : 'none'}}>
                <p className="text-sm text-white/60 uppercase tracking-wider mb-2">Con Mobilsafe conseguirías</p>
                <p className="text-4xl md:text-5xl font-black text-yellow-400" style={{textShadow: '0 0 40px rgba(250,204,21,0.6)'}}>
                  {animatedMoney.toLocaleString('es-ES')} €
                </p>
                <p className="text-sm text-white/70 mt-1">de ingresos CAES</p>
                
                <div className="w-3/4 h-px bg-gradient-to-r from-transparent via-yellow-400/50 to-transparent my-4"></div>
                
                <div className="flex items-center gap-3">
                  <span className="text-3xl">🛢️</span>
                  <div>
                    <p className="text-2xl md:text-3xl font-bold text-green-400">
                      {animatedLiters.toLocaleString('es-ES')} L
                    </p>
                    <p className="text-xs text-white/50">de combustible ahorrados</p>
                  </div>
                </div>
              </div>
            </div>

            {/* IMPL-056 · Bloque "¿Por qué el doble?" — reemplaza las 3 razones anteriores */}
            <div
              className="mt-6"
              style={{
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '10px',
                padding: '12px 14px',
              }}
              data-testid="why-doble-box"
            >
              <p style={{ fontSize: '11px', fontWeight: 700, color: '#fbbf24', marginBottom: '8px' }}>
                ¿Por qué el doble?
              </p>
              {[
                'Maximizamos la TRA-020 al 100% — la mayoría solo extrae el 25%',
                '90 días de acompañamiento real en conducción eficiente',
                'Descubrimos costes ocultos y mejoras que otros no ven',
                'Acuerdos marco — precio superior al mercado spot',
              ].map((txt, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-2"
                  style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', marginBottom: '6px', lineHeight: 1.4 }}
                  data-testid={`why-doble-item-${idx + 1}`}
                >
                  <span style={{ color: '#fbbf24', fontSize: '14px', marginTop: '-1px', lineHeight: 1 }}>›</span>
                  <span>{txt}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ========== SECCIÓN 3: CTA REUNIÓN (IMPL-057) ========== */}
          {/* IMPL-057 · Eliminado el formulario duplicado de empresa/email/teléfono.
              Los datos ya se capturaron en pre-registro (email + teléfono obligatorios),
              `leadData` está poblado desde el primer paso del flujo. */}
          <div className="bg-white/10 backdrop-blur border-2 border-yellow-400/40 rounded-2xl p-6">
            <h3 className="text-xl font-black text-center mb-4">
              ¿Quieres saber cuánto puedes<br/>
              conseguir con <span className="text-yellow-400">MOBILSAFE</span>?
            </h3>

            <button 
              onClick={handleSubmitLead}
              disabled={sending}
              data-testid="partial-result-cta-reunion"
              className="w-full py-4 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 rounded-xl font-black text-lg uppercase tracking-wide shadow-lg shadow-orange-500/30 flex items-center justify-center gap-3 disabled:opacity-50 transition-all"
            >
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Phone className="w-5 h-5" />}
              {sending ? 'Enviando...' : 'Quiero activarlo en mi flota'}
            </button>

            <p className="text-center text-orange-200/50 text-xs mt-4 flex items-center justify-center gap-2">
              <Shield className="w-4 h-4" /> Te contactamos en 24h laborables
            </p>
          </div>

          {/* IMPL-056 · botón "Volver a modificar datos" eliminado intencionalmente.
              Una vez visto el resultado, no queremos que el usuario retroceda y modifique
              el cálculo sin agendar la reunión. */}
        </div>

        {/* Footer con enlaces legales */}
        <footer className="py-6 border-t border-orange-500/20">
          <div className="max-w-xl mx-auto px-6 text-center">
            <div className="flex flex-wrap justify-center gap-4 mb-3 text-xs">
              <a href="https://www.mobilsafe.es/condiciones-de-uso-y-privacidad/" target="_blank" rel="noopener noreferrer" className="text-orange-300/50 hover:text-orange-300 transition-all">
                Condiciones de uso y privacidad
              </a>
              <span className="text-orange-500/30">|</span>
              <a href="https://www.mobilsafe.es/politica-de-cookies/" target="_blank" rel="noopener noreferrer" className="text-orange-300/50 hover:text-orange-300 transition-all">
                Política de cookies
              </a>
            </div>
            <p className="text-orange-200/50 text-sm">© 2026 MOBILSAFE Fleet Technology</p>
          </div>
        </footer>
      </div>
    );
  }

  // ==================== VISTA: SELECCIÓN DE CITA ====================
  if (currentView === 'select-appointment') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 via-orange-800 to-red-900 text-white">
        {/* Header */}
        <header className="bg-black/20 backdrop-blur-lg">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
            <button onClick={() => setCurrentView('partial-result')} className="p-2 hover:bg-white/10 rounded-lg transition-all">
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center shadow-lg">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-black text-white">RESERVA TU CITA</h1>
              <p className="text-sm text-orange-200/70">Elige fecha y hora</p>
            </div>
          </div>
        </header>

        <div className="max-w-xl mx-auto px-6 py-8">
          {/* Título */}
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-black leading-tight">
              ¡Perfecto, <span className="text-yellow-400">{leadData.empresa || 'tu empresa'}</span>!
            </h2>
            <p className="text-orange-100/80 mt-3">
              Elige cuándo quieres que te llamemos para explicarte<br/>
              cómo conseguir <span className="font-bold text-yellow-400">+50% más</span> en tus CAES
            </p>
          </div>

          {loadingSlots ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-10 h-10 animate-spin text-yellow-400 mb-4" />
              <p className="text-orange-100/70">Cargando horarios disponibles...</p>
            </div>
          ) : (
            <>
              {/* Selector de fecha */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-orange-100 mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-yellow-400" />
                  Selecciona un día
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[200px] overflow-y-auto pr-2">
                  {disponibilidad.slice(0, 12).map((dia) => (
                    <button
                      key={dia.fecha}
                      onClick={() => {
                        setSelectedDate(dia.fecha);
                        setSelectedTime(null);
                      }}
                      className={`p-3 rounded-xl border-2 transition-all text-left ${
                        selectedDate === dia.fecha
                          ? 'bg-yellow-500/30 border-yellow-400 text-yellow-400'
                          : 'bg-white/5 border-orange-400/30 hover:border-orange-400/60'
                      }`}
                    >
                      <div className="text-xs text-orange-200/70">{dia.diaSemana}</div>
                      <div className="font-bold">
                        {new Date(dia.fecha).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                      </div>
                      <div className="text-xs text-orange-200/50">{dia.slots.length} horas</div>
                    </button>
                  ))}
                </div>
                {disponibilidad.length > 12 && (
                  <p className="text-center text-orange-200/50 text-xs mt-2">
                    Mostrando los próximos 12 días con disponibilidad
                  </p>
                )}
              </div>

              {/* Selector de hora */}
              {selectedDate && (
                <div className="mb-8">
                  <label className="block text-sm font-semibold text-orange-100 mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-yellow-400" />
                    Selecciona una hora
                  </label>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {disponibilidad.find(d => d.fecha === selectedDate)?.slots.map((hora) => (
                      <button
                        key={hora}
                        onClick={() => setSelectedTime(hora)}
                        className={`py-3 px-4 rounded-xl border-2 font-bold transition-all ${
                          selectedTime === hora
                            ? 'bg-yellow-500/30 border-yellow-400 text-yellow-400'
                            : 'bg-white/5 border-orange-400/30 hover:border-orange-400/60'
                        }`}
                      >
                        {hora}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Resumen y botón de confirmar */}
              {selectedDate && selectedTime && (
                <div className="bg-white/10 backdrop-blur border-2 border-yellow-400/40 rounded-2xl p-6 mb-6">
                  <div className="text-center mb-4">
                    <p className="text-orange-100/70 text-sm mb-2">Tu cita</p>
                    <p className="text-2xl font-black text-yellow-400">
                      {disponibilidad.find(d => d.fecha === selectedDate)?.diaSemana}{' '}
                      {new Date(selectedDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}
                    </p>
                    <p className="text-3xl font-black text-white mt-1">{selectedTime}h</p>
                    <p className="text-orange-200/50 text-sm mt-2">Duración: 45 minutos</p>
                  </div>
                  
                  <button
                    onClick={handleConfirmAppointment}
                    disabled={sending}
                    className="w-full py-4 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 rounded-xl font-black text-lg uppercase tracking-wide shadow-lg shadow-orange-500/30 flex items-center justify-center gap-3 disabled:opacity-50 transition-all"
                  >
                    {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                    {sending ? 'Confirmando...' : 'Confirmar mi cita'}
                  </button>
                </div>
              )}

              {/* Nota */}
              <p className="text-center text-orange-200/50 text-xs flex items-center justify-center gap-2">
                <Shield className="w-4 h-4" /> Te llamaremos al teléfono {leadData.telefono}
              </p>
            </>
          )}
        </div>

        {/* Footer */}
        <footer className="py-6 border-t border-orange-500/20">
          <div className="max-w-xl mx-auto px-6 text-center">
            <div className="flex flex-wrap justify-center gap-4 mb-3 text-xs">
              <a href="https://www.mobilsafe.es/condiciones-de-uso-y-privacidad/" target="_blank" rel="noopener noreferrer" className="text-orange-300/50 hover:text-orange-300 transition-all">
                Condiciones de uso y privacidad
              </a>
              <span className="text-orange-500/30">|</span>
              <a href="https://www.mobilsafe.es/politica-de-cookies/" target="_blank" rel="noopener noreferrer" className="text-orange-300/50 hover:text-orange-300 transition-all">
                Política de cookies
              </a>
            </div>
            <p className="text-orange-200/50 text-sm">© 2026 MOBILSAFE Fleet Technology</p>
          </div>
        </footer>
      </div>
    );
  }

  // ==================== VISTA: CONFIRMACIÓN ====================
  if (currentView === 'confirmation') {
    const fechaFormateada = citaConfirmada 
      ? new Date(citaConfirmada.fecha).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })
      : '';
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 via-orange-800 to-red-900 text-white">
        {/* Header */}
        <header className="bg-black/20 backdrop-blur-lg">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg">
              <Check className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-black text-white">¡CITA CONFIRMADA!</h1>
              <p className="text-sm text-green-200/70">Te esperamos</p>
            </div>
          </div>
        </header>

        <div className="max-w-xl mx-auto px-6 py-12">
          <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-2 border-green-500/40 rounded-2xl p-8 text-center">
            <div className="w-20 h-20 bg-green-500/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Calendar className="w-10 h-10 text-green-400" />
            </div>
            <h2 className="text-2xl font-black text-green-400 mb-2">¡Tu reunión está confirmada!</h2>
            <p className="text-green-300/70 mb-6">Hemos enviado un email de confirmación a {leadData.email}</p>
            
            {citaConfirmada && (
              <div className="bg-green-500/20 rounded-2xl p-6 mb-6">
                <p className="text-green-300/70 text-sm mb-2">{citaConfirmada.diaSemana}</p>
                <p className="text-3xl font-black text-white mb-1">{fechaFormateada}</p>
                <p className="text-4xl font-black text-yellow-400">{citaConfirmada.hora}h</p>
                <p className="text-green-300/50 text-sm mt-3">Duración: 45 minutos</p>
              </div>
            )}
            
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6">
              <p className="text-yellow-400 font-semibold flex items-center justify-center gap-2">
                <Phone className="w-5 h-5" /> Te llamaremos al {leadData.telefono}
              </p>
              <p className="text-yellow-300/70 text-sm mt-1">
                Prepara tus dudas sobre los certificados CAES
              </p>
            </div>
            
            <div className="grid grid-cols-1 gap-3 text-left mb-6">
              <div className="bg-white/5 rounded-xl p-4 flex items-start gap-3">
                <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-green-400" />
                </div>
                <div>
                  <p className="font-semibold text-white">Análisis personalizado</p>
                  <p className="text-sm text-green-300/70">Revisaremos tu potencial CAES de {caesEstimado.toLocaleString('es-ES')}€</p>
                </div>
              </div>
              <div className="bg-white/5 rounded-xl p-4 flex items-start gap-3">
                <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-4 h-4 text-green-400" />
                </div>
                <div>
                  <p className="font-semibold text-white">Cómo conseguimos +50%</p>
                  <p className="text-sm text-green-300/70">Te explicamos nuestra metodología diferencial</p>
                </div>
              </div>
              <div className="bg-white/5 rounded-xl p-4 flex items-start gap-3">
                <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users className="w-4 h-4 text-green-400" />
                </div>
                <div>
                  <p className="font-semibold text-white">Sin compromiso</p>
                  <p className="text-sm text-green-300/70">Primera consulta 100% gratuita</p>
                </div>
              </div>
            </div>
            
            <div className="border-t border-green-500/30 pt-6">
              <p className="text-green-300/60 text-sm mb-4">¿Necesitas cambiar la cita? Responde al email de confirmación</p>
              <button
                onClick={() => {
                  setCurrentView('landing');
                  setLeadData({ empresa: '', email: '', telefono: '' });
                  setCitaConfirmada(null);
                  setSelectedDate(null);
                  setSelectedTime(null);
                }}
                className="px-6 py-3 bg-green-500/20 hover:bg-green-500/30 border border-green-500/40 rounded-xl font-semibold text-green-400 transition-all"
              >
                Volver al inicio
              </button>
            </div>
          </div>
        </div>

        {/* Footer con enlaces legales */}
        <footer className="py-6 border-t border-green-500/20">
          <div className="max-w-xl mx-auto px-6 text-center">
            <div className="flex flex-wrap justify-center gap-4 mb-3 text-xs">
              <a href="https://www.mobilsafe.es/condiciones-de-uso-y-privacidad/" target="_blank" rel="noopener noreferrer" className="text-green-300/50 hover:text-green-300 transition-all">
                Condiciones de uso y privacidad
              </a>
              <span className="text-green-500/30">|</span>
              <a href="https://www.mobilsafe.es/politica-de-cookies/" target="_blank" rel="noopener noreferrer" className="text-green-300/50 hover:text-green-300 transition-all">
                Política de cookies
              </a>
            </div>
            <p className="text-green-200/50 text-sm">© 2026 MOBILSAFE Fleet Technology</p>
          </div>
        </footer>
      </div>
    );
  }

  // ==================== VISTA: LANDING (por defecto) ====================
  return (
    <div className="min-h-screen bg-white text-slate-800">
      
      {/* HEADER */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 sm:py-4 flex justify-between items-center">
          <button onClick={onBack} className="flex items-center gap-2 sm:gap-3 hover:opacity-80 transition-all group">
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 group-hover:-translate-x-1 transition-transform" />
            <span className="text-slate-600 font-medium text-xs sm:text-base hidden sm:inline">CALCULA MÁS AHORROS CON</span>
            <img src={MOBILSAFE_LOGO} alt="MOBILSAFE" className="h-6 sm:h-8" />
          </button>
          <button 
            onClick={goToCalculator}
            data-testid="caes-cta-header"
            className="px-4 sm:px-6 py-1.5 sm:py-2 bg-gradient-to-r from-[#ec561c] to-[#d44a15] hover:from-[#d44a15] hover:to-[#b8400f] text-white rounded-full font-bold text-xs sm:text-sm uppercase tracking-wide shadow-lg shadow-orange-500/20 transition-all"
          >
            Calcula tu Ingreso CAES
          </button>
        </div>
      </header>

      {/* Spacer para el header fixed */}
      <div className="h-12 sm:h-16"></div>

      {/* BANNER MITECO - Fondo VERDE para diferenciar */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 border-b border-emerald-800">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2.5">
          <div className="flex items-center justify-between">
            {/* MITECO Logo */}
            <div className="flex items-center gap-2 pr-3 border-r border-emerald-500/40">
              <span className="text-lg sm:text-xl">🏛️</span>
              <div>
                <p className="text-sm font-bold text-white leading-tight">MITECO</p>
                <p className="text-xs text-emerald-200 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-emerald-300 rounded-full animate-pulse"></span>
                  Datos oficiales
                </p>
              </div>
            </div>
            
            {/* Todos CAES */}
            <div className="flex items-center gap-2 sm:gap-3 bg-white/15 backdrop-blur border border-white/20 rounded-lg px-2 sm:px-4 py-1.5">
              <span className="text-xs font-semibold text-emerald-100 bg-white/20 px-2 py-0.5 rounded hidden sm:inline">Todos CAES</span>
              <div className="flex items-center gap-2 sm:gap-4">
                <div className="text-center">
                  <p className="text-sm sm:text-base font-bold text-white">+8.000 GWh</p>
                  <p className="text-xs text-emerald-200 hidden sm:block">certificados</p>
                </div>
                <div className="text-center border-l border-white/20 pl-2 sm:pl-4">
                  <p className="text-sm sm:text-base font-bold text-white">+6.000</p>
                  <p className="text-xs text-emerald-200 hidden sm:block">actuaciones</p>
                </div>
              </div>
            </div>
            
            {/* Solo TRA-020 */}
            <div className="flex items-center gap-2 sm:gap-3 bg-yellow-400/20 backdrop-blur border border-yellow-400/30 rounded-lg px-2 sm:px-4 py-1.5">
              <span className="text-xs font-semibold text-yellow-200 bg-yellow-500/30 px-2 py-0.5 rounded hidden sm:inline">TRA-020</span>
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="text-center">
                  <p className="text-lg sm:text-xl font-black text-yellow-300">250</p>
                  <p className="text-xs text-emerald-200 hidden sm:block">actuaciones</p>
                </div>
                <p className="text-xs sm:text-sm text-yellow-200 font-medium hidden md:block">la más rentable</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* HERO SECTION */}
      <section className="pt-8 pb-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/50 via-transparent to-green-50/30" />
        <div className="max-w-6xl mx-auto relative">
          
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-emerald-100 border border-emerald-300 px-4 py-2 rounded-full mb-4">
            <Trophy className="w-4 h-4 text-emerald-600" />
            <span className="text-emerald-700 text-sm font-bold uppercase tracking-wide">1ª Empresa en monetizar TRA-020</span>
          </div>
          <div className="inline-flex items-center gap-2 bg-orange-100 border border-orange-300 px-4 py-2 rounded-full mb-8 ml-0 md:ml-4">
            <AlertTriangle className="w-4 h-4 text-orange-600" />
            <span className="text-orange-700 text-sm font-bold uppercase tracking-wide">La TRA-020 es la ficha más difícil. Nosotros la dominamos.</span>
          </div>

          {/* Headline */}
          <h1 className="text-5xl md:text-7xl font-black leading-tight mb-6 text-slate-900">
            MONETIZA LA EFICIENCIA<br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-green-500">DE TU FLOTA</span>
          </h1>

          <p className="text-xl md:text-2xl text-slate-500 max-w-3xl mb-8 leading-relaxed">
            Transformamos la reducción de consumo en <strong className="text-slate-800">ingresos certificables (CAES)</strong>. 
            Tecnología de conducción eficiente con resultados medibles y verificables.
          </p>

          {/* Stats */}
          <div className="flex flex-wrap gap-6 mb-10">
            <div className="bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4">
              <p className="text-4xl font-black text-emerald-600">8%</p>
              <p className="text-sm text-slate-500 uppercase">Reducción media verificada</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4">
              <p className="text-4xl font-black text-emerald-600">+500</p>
              <p className="text-sm text-slate-500 uppercase">Flotas monitorizadas</p>
            </div>
            <div className="bg-slate-50 border border-orange-200 rounded-2xl px-6 py-4">
              <p className="text-4xl font-black text-orange-600">90 días</p>
              <p className="text-sm text-orange-700 uppercase">Contigo. No te dejamos solo.</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4">
              <p className="text-4xl font-black text-emerald-600">TRA-020</p>
              <p className="text-sm text-slate-500 uppercase">Ficha homologada</p>
            </div>
            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-300 rounded-2xl px-6 py-4">
              <p className="text-sm text-amber-700 uppercase mb-1">Hasta</p>
              <p className="text-4xl font-black text-amber-600">2.500€</p>
              <p className="text-sm text-amber-700">por vehículo</p>
            </div>
          </div>

          {/* CTA */}
          <button 
            onClick={goToCalculator}
            data-testid="caes-cta-hero"
            className="group px-10 py-5 bg-gradient-to-r from-[#ec561c] to-[#d44a15] hover:from-[#d44a15] hover:to-[#b8400f] text-white rounded-2xl font-black text-xl uppercase tracking-wide shadow-2xl shadow-orange-500/30 hover:shadow-orange-500/50 transition-all transform hover:scale-105 flex items-center gap-3"
          >
            <Flame className="w-6 h-6" />
            Calcula tu Ingreso CAES
            <ChevronRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black text-center mb-4 text-slate-900">EL CONTEXTO ACTUAL</h2>
          <p className="text-slate-500 text-center mb-12 text-lg">Lo que vemos en la mayoría de flotas</p>
          
          <div className="grid md:grid-cols-4 gap-6 mb-12">
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                <Fuel className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-slate-800">Dispersión de consumos</h3>
              <p className="text-slate-500 text-sm">Diferencias de hasta 20% entre conductores del mismo vehículo y ruta. Sin datos, no hay gestión posible.</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-slate-800">Potencial sin explotar</h3>
              <p className="text-slate-500 text-sm">La mayoría de flotas tienen un margen de mejora del 8-15% en eficiencia de combustible.</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                <Euro className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-slate-800">CAES desaprovechados</h3>
              <p className="text-slate-500 text-sm">Los Certificados de Ahorro Energético permiten monetizar la reducción de consumo. Pocos lo están haciendo.</p>
            </div>
            <div className="bg-white border border-orange-200 rounded-2xl p-6">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-orange-700">El riesgo de hacerlo mal</h3>
              <p className="text-slate-500 text-sm">Si no gestionas bien los CAES, puedes perder <strong className="text-orange-600">hasta el 50%</strong> del ingreso potencial. La TRA-020 no perdona errores.</p>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-3xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-black mb-4 text-slate-900">El mecanismo CAES</h3>
                <p className="text-slate-500 leading-relaxed mb-4">
                  El Real Decreto 36/2023 obliga a las comercializadoras de energía a adquirir certificados de ahorro energético. 
                  La reducción verificable de consumo en flotas genera estos certificados.
                </p>
                <p className="text-slate-500 leading-relaxed">
                  <strong className="text-emerald-600">MOBILSAFE</strong> es actuación elegible bajo la ficha <strong className="text-slate-800">TRA-020</strong> 
                  (Conducción eficiente de vehículos de transporte por carretera).
                </p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
                <p className="text-sm text-slate-400 uppercase mb-2">Esquema simplificado</p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600 font-bold text-sm">1</div>
                    <span className="text-sm text-slate-700">Implementación de tecnología</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600 font-bold text-sm">2</div>
                    <span className="text-sm text-slate-700">Reducción verificada de consumo</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600 font-bold text-sm">3</div>
                    <span className="text-sm text-slate-700">Generación de certificados CAES</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600 font-bold text-sm">4</div>
                    <span className="text-sm text-slate-700">Venta a sujetos obligados</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DIFFERENTIATION SECTION */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black text-center mb-4 text-slate-900">NUESTRA METODOLOGÍA</h2>
          <p className="text-emerald-600 text-center mb-12 text-xl font-bold">Conducción eficiente con resultados verificables</p>
          
          <div className="bg-white rounded-3xl overflow-hidden border border-slate-200 shadow-sm">
            <div className="grid md:grid-cols-2">
              {/* Enfoque convencional */}
              <div className="p-8 border-b md:border-b-0 md:border-r border-slate-200">
                <h3 className="text-xl font-bold text-slate-400 mb-6 flex items-center gap-2">
                  Enfoque convencional
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3 text-slate-500">
                    <X className="w-5 h-5 text-slate-400 mt-1 flex-shrink-0" />
                    <span>Instalación de dispositivo sin seguimiento posterior</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-500">
                    <X className="w-5 h-5 text-slate-400 mt-1 flex-shrink-0" />
                    <span>Precio de CAES dependiente del mercado spot</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-500">
                    <X className="w-5 h-5 text-slate-400 mt-1 flex-shrink-0" />
                    <span>Sin metodología de cambio de comportamiento</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-500">
                    <X className="w-5 h-5 text-slate-400 mt-1 flex-shrink-0" />
                    <span>Gestión administrativa sin foco en resultados</span>
                  </li>
                </ul>
              </div>
              
              {/* Enfoque MOBILSAFE */}
              <div className="p-8 bg-emerald-50">
                <h3 className="text-xl font-bold text-emerald-600 mb-6 flex items-center gap-2">
                  <BadgeCheck className="w-5 h-5" /> Metodología MOBILSAFE
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3 text-slate-700">
                    <Check className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <span><strong className="text-emerald-600">90 días pegados a ti.</strong> Seguimiento semanal real, no un email automático</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-700">
                    <Check className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <span><strong className="text-emerald-600">Expertos en TRA-020.</strong> La ficha más difícil, pero la más rentable</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-700">
                    <Check className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <span><strong className="text-emerald-600">Optimizamos cada euro.</strong> Acuerdos marco con sujetos obligados</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-700">
                    <Check className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
                    <span><strong className="text-emerald-600">Visión 360º.</strong> Tu análisis revela oportunidades en seguridad, mantenimiento y operativa</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS SECTION */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black text-center mb-4 text-slate-900">90 DÍAS. NI UNO SOLO SIN NOSOTROS.</h2>
          <p className="text-emerald-600 text-center mb-12 text-lg font-semibold">Por eso funciona</p>
          
          <div className="grid md:grid-cols-4 gap-6 mb-12">
            {[
              { day: "SEMANA 1", icon: <Target className="w-8 h-8" />, title: "Diagnóstico", desc: "Análisis de flota, definición de baseline y objetivos" },
              { day: "SEMANA 2", icon: <Truck className="w-8 h-8" />, title: "Despliegue", desc: "Instalación de dispositivos y formación inicial" },
              { day: "SEMANA 3-12", icon: <TrendingUp className="w-8 h-8" />, title: "Optimización", desc: "Seguimiento semanal, coaching y ajuste de parámetros" },
              { day: "CONTINUO", icon: <Euro className="w-8 h-8" />, title: "Monetización", desc: "Certificación de ahorros y gestión de CAES" }
            ].map((step, i) => (
              <div key={i} className="relative">
                <div className="bg-white rounded-2xl p-6 border border-slate-200 h-full">
                  <div className="text-emerald-600 font-bold text-xs mb-3 uppercase tracking-wide">{step.day}</div>
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center mb-4 text-white">
                    {step.icon}
                  </div>
                  <h3 className="font-bold text-lg mb-2 text-slate-800">{step.title}</h3>
                  <p className="text-slate-500 text-sm">{step.desc}</p>
                </div>
                {i < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <ChevronRight className="w-6 h-6 text-emerald-500" />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Technical Details */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h4 className="font-bold text-lg mb-4 flex items-center gap-2 text-slate-800">
                <Fuel className="w-5 h-5 text-emerald-600" /> Variables monitorizadas
              </h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> Consumo instantáneo y acumulado</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> Ralentí y tiempo de motor encendido</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> Patrones de aceleración y frenada</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> Uso de inercia y anticipación</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> Velocidad media y excesos</li>
              </ul>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h4 className="font-bold text-lg mb-4 flex items-center gap-2 text-slate-800">
                <TrendingUp className="w-5 h-5 text-emerald-600" /> Entregables del programa
              </h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> Dashboard de seguimiento en tiempo real</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> Informes semanales por conductor</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> Ranking y sistema de incentivos</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> Certificación de ahorro energético</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> Gestión completa de expediente CAES</li>
              </ul>
            </div>
          </div>

          <div className="text-center mt-10">
            <button 
              onClick={goToCalculator}
              data-testid="caes-cta-mid"
              className="px-8 py-4 bg-gradient-to-r from-[#ec561c] to-[#d44a15] hover:from-[#d44a15] hover:to-[#b8400f] text-white rounded-xl font-bold text-lg uppercase shadow-lg shadow-orange-500/20 transition-all inline-flex items-center gap-2"
            >
              <Flame className="w-5 h-5" /> Calcula tu Ingreso CAES
            </button>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-black mb-12 text-slate-900">RESULTADOS VERIFICADOS</h2>
          
          <div className="grid md:grid-cols-4 gap-6 mb-12">
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
              <p className="text-4xl font-black text-emerald-600 mb-2">8,2%</p>
              <p className="text-slate-500 text-sm">Reducción media de consumo en programa completo</p>
            </div>
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
              <p className="text-4xl font-black text-emerald-600 mb-2">+500</p>
              <p className="text-slate-500 text-sm">Flotas con programa implementado</p>
            </div>
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
              <p className="text-4xl font-black text-emerald-600 mb-2">15.000</p>
              <p className="text-slate-500 text-sm">Conductores formados en conducción eficiente</p>
            </div>
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
              <p className="text-4xl font-black text-emerald-600 mb-2">94%</p>
              <p className="text-slate-500 text-sm">Tasa de renovación de clientes</p>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-3xl p-8 md:p-12">
            <p className="text-sm text-emerald-600 uppercase tracking-wide mb-4">Caso de éxito verificado</p>
            <blockquote className="text-xl md:text-2xl text-slate-600 mb-6 max-w-3xl mx-auto">
              "MOBILSAFE nos ayudó a convertirnos en la <span className="text-emerald-600 font-bold">primera empresa de transporte de pasajeros</span> en certificar CAEs en España. Ahorro verificado: <span className="text-emerald-600 font-bold">51.629 litros de gasóleo al año</span>."
            </blockquote>
            <p className="text-slate-500">
              — <strong className="text-slate-800">VECTALIA</strong>, Operador de transporte público, Alicante
            </p>
          </div>
        </div>
      </section>

      {/* VALOR AÑADIDO SECTION */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black text-center mb-4 text-slate-900">TU ANÁLISIS CAES TE DA MÁS QUE DINERO</h2>
          <p className="text-slate-500 text-center mb-12 text-lg">Cuando analizamos tu flota para CAES, descubrimos oportunidades en:</p>
          
          <div className="grid md:grid-cols-4 gap-6 mb-10">
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-7 h-7 text-red-500" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-red-600">Seguridad</h3>
              <p className="text-slate-500 text-sm">Reducción de accidentes y siniestralidad</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Wrench className="w-7 h-7 text-blue-500" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-blue-600">Mantenimiento</h3>
              <p className="text-slate-500 text-sm">Anticipación de averías y reducción de costes</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Activity className="w-7 h-7 text-purple-500" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-purple-600">Operativa</h3>
              <p className="text-slate-500 text-sm">Eficiencias ocultas en rutas y tiempos</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Leaf className="w-7 h-7 text-emerald-500" />
              </div>
              <h3 className="font-bold text-lg mb-2 text-emerald-600">Sostenibilidad</h3>
              <p className="text-slate-500 text-sm">Reducción de huella de carbono certificable</p>
            </div>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-2xl p-8 text-center">
            <p className="text-xl md:text-2xl font-bold text-orange-800">
              El CAES es la puerta. <span className="text-amber-600">El potencial real está detrás.</span>
            </p>
          </div>
        </div>
      </section>

      {/* CTA FINAL SECTION */}
      <section className="py-20 px-6 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-black mb-4 text-slate-900">
            DESCUBRE TODO EL POTENCIAL DE TU FLOTA
          </h2>
          <p className="text-slate-600 mb-4 text-lg">
            Cada vehículo puede generar entre <span className="text-amber-600 font-bold">500€ y 2.500€</span>
          </p>
          <p className="text-slate-400 mb-10 text-base">
            por actuación verificada, según tipo de vehículo y kilómetros
          </p>

          <button
            onClick={goToCalculator}
            data-testid="caes-cta-footer"
            className="px-12 py-6 bg-gradient-to-r from-[#ec561c] to-[#d44a15] hover:from-[#d44a15] hover:to-[#b8400f] text-white rounded-2xl font-black text-2xl uppercase tracking-wide shadow-2xl shadow-orange-500/30 hover:shadow-orange-500/50 transition-all transform hover:scale-105 inline-flex items-center gap-4"
          >
            <Flame className="w-8 h-8" />
            Calcula tu Ingreso CAES
            <ChevronRight className="w-8 h-8" />
          </button>
          
          <p className="text-slate-400 text-sm mt-6">
            Sin compromiso. Análisis preliminar en 2 minutos.
          </p>
        </div>
      </section>

      {/* POWER BI MITECO EMBEBIDO */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-blue-100 border border-blue-300 px-4 py-2 rounded-full mb-4">
              <span className="text-xl">🏛️</span>
              <span className="text-blue-700 text-sm font-bold uppercase tracking-wide">Datos Oficiales del Gobierno de España</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-black text-slate-900 mb-3">
              PANEL INTERACTIVO DEL <span className="text-blue-600">MITECO</span>
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto">
              Consulta en tiempo real los certificados de ahorro energético emitidos. 
              <span className="text-emerald-600 font-semibold"> Filtra por "Transporte"</span> para ver el potencial de la ficha TRA-020.
            </p>
          </div>

          {/* Iframe del Power BI */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-blue-200">
            <iframe 
              title="Panel CAE MITECO"
              width="100%" 
              height="500" 
              src="https://app.powerbi.com/view?r=eyJrIjoiZThmMjBlY2ItNjU3Yi00YTBjLWIzZjEtYTlhMjY1ZjFiMGY3IiwidCI6ImRlODFhNTc4LTgxYWEtNGQzNS1hNWQ0LWZlZWQ4MTZiMTY1NCIsImMiOjl9" 
              frameBorder="0" 
              allowFullScreen={true}
              className="w-full"
            />
          </div>

          {/* Nota inferior */}
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
            <p className="text-slate-400">
              Fuente: Ministerio para la Transición Ecológica y el Reto Demográfico
            </p>
            <a 
              href="https://www.miteco.gob.es/es/energia/eficiencia/cae/seguimiento-del-sistema-de-cae.html" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-600 flex items-center gap-1 transition-colors"
            >
              Ver página oficial <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-black text-center mb-4 text-slate-900">PREGUNTAS FRECUENTES</h2>
          <p className="text-slate-500 text-center mb-12 text-lg">Respuestas honestas a las dudas más comunes</p>
          
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div 
                key={i} 
                className="bg-white border border-slate-200 rounded-xl overflow-hidden"
              >
                <button 
                  onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-emerald-50 transition-all"
                >
                  <span className="font-bold text-lg pr-4 text-slate-800">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-emerald-600 transition-transform flex-shrink-0 ${expandedFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {expandedFaq === i && (
                  <div className="px-6 pb-4 text-slate-600 leading-relaxed border-t border-slate-200 pt-4">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER DE CONFIANZA */}
      <section className="py-12 px-6">
        <div className="max-w-2xl mx-auto">
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
            <div className="flex flex-wrap justify-center gap-8 mb-4">
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 bg-white border border-slate-200 rounded-full flex items-center justify-center text-xl">🏛️</div>
                <span className="text-xs text-slate-400">Datos MITECO</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 bg-white border border-slate-200 rounded-full flex items-center justify-center text-xl">📜</div>
                <span className="text-xs text-slate-400">RD 36/2023</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 bg-white border border-slate-200 rounded-full flex items-center justify-center text-xl">🎯</div>
                <span className="text-xs text-slate-400">Expertos TRA-020</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 bg-white border border-slate-200 rounded-full flex items-center justify-center text-xl">🔒</div>
                <span className="text-xs text-slate-400">RGPD Compliant</span>
              </div>
            </div>
            <p className="text-center text-xs text-slate-400">
              Fuente: <a href="https://datos.gob.es/es/catalogo/ea0010587-certificados-de-ahorro-energetico-cae" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700">datosabiertos.miteco.gob.es</a> • 
              Marco legal: <a href="https://www.boe.es/buscar/act.php?id=BOE-A-2023-628" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700">RD 36/2023</a>
            </p>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-12 px-6 border-t border-slate-200">
        <div className="max-w-6xl mx-auto text-center">
          <img src={MOBILSAFE_LOGO} alt="MOBILSAFE" className="h-10 mx-auto mb-4 opacity-70" />
          <p className="text-emerald-600 mb-4">Pioneros en monetización de conducción eficiente en España</p>
          <div className="flex flex-wrap justify-center gap-4 mb-4 text-sm">
            <a 
              href="https://www.mobilsafe.es/condiciones-de-uso-y-privacidad/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-emerald-600 transition-all"
            >
              Condiciones de uso y privacidad
            </a>
            <span className="text-slate-300">|</span>
            <a 
              href="https://www.mobilsafe.es/politica-de-cookies/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-emerald-600 transition-all"
            >
              Política de cookies
            </a>
          </div>
          <p className="text-slate-400 text-sm">© 2026 MOBILSAFE Fleet Technology</p>
        </div>
      </footer>
    </div>
  );
};

export default CAESLandingNew;
